import _isEqual from 'lodash/isEqual';
import _isMatch from 'lodash/isMatch';
import _merge from 'lodash/merge';
import { IShapeModel } from '../../element-modeles/Interface/shape-model.interface';
import { FontScaleDependentValues, getFontScale, updateFontScaleSafety } from '../../element-modeles/property/text/text-adjust/font-scale-adjust';
import TextAdjustManager, { ShapeSizeDependentValues } from '../../element-modeles/property/text/text-adjust';
import { AutoFitType } from '../../element-modeles/property/text/constants/text-style-type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { ShapePropertyConverter } from '../../converter/node-property/element/shape-property-converter';

/**
 * 管理 textAdjust 属性相关的副作用，包含两种
 * 1. 自缩排
 * 2. 根据文字调整形状大小
 */
export default class TextAdjustEffectManager {
    /**
     * 校验是否需要重新计算 textAdjust 属性相关的副作用，如果需要就执行计算逻辑
     */
    public static checkAndRecalculateTextAdjustEffect(
        originalShapeModel: IShapeModel,
        updatedShapeModel: Partial<IShapeModel>,
    ) {
        if (this.shouldRecalculateFontScale(originalShapeModel, updatedShapeModel)) {
            this.handleFontScaleRecalculation(originalShapeModel, updatedShapeModel);
        }
        if (this.shouldRecalculateShapeSize(originalShapeModel, updatedShapeModel)) {
            this.handleShapeSizeRecalculation(originalShapeModel, updatedShapeModel);
        }
    }

    /**
     * 处理 fontScale 需要重新计算时的逻辑
     * @param originalShapeModel
     * @param updatedShapeModel
     */
    private static handleFontScaleRecalculation(
        originalShapeModel: IShapeModel,
        updatedShapeModel: Partial<IShapeModel>,
    ) {
        const isLevelChange =  this.hasParagraphLevelChanged(originalShapeModel, updatedShapeModel);
        if (isLevelChange && originalShapeModel.slideRef) {
            // 3.0 之后文字属性的继承是在 sdk 中进行，所以 level 改变后，paragraphList 里面部分文字属性已经过时了
            // 所以需要重新走一下 sdk 的继承逻辑拿到新的 shapeModel
            const shapeModel = this.getShapeModelWithInherit(originalShapeModel, updatedShapeModel);
            // 使用继承后数据准确的 shapeModel 进行计算
            this.recalculateAndUpdateFontScale(shapeModel);
        } else {
            // 如果 level 没变，那么认为 updatedShapeModel 上的文字属性是准确的，可以基于这个数据进行计算
            const { textBodyStyle, paragraphList, props } = updatedShapeModel;
            // 如果要修改的数据中有 props 的话，把 originalShapeModel 和 props 合并生成新的数据，基于这个数据计算
            // 这样能够避免计算时使用了过时的 props 数据
            const shapeModel = props ? _merge(
                Object.create(Object.getPrototypeOf(originalShapeModel)),
                originalShapeModel, { props: updatedShapeModel?.props },
            ) : originalShapeModel;
            this.recalculateAndUpdateFontScale(shapeModel, {
                textBodyStyle,
                paragraphList,
                style: props?.style,
            });
        }
    }

    /**
     * 处理 shapeSize 需要重新计算时的逻辑
     * @param originalShapeModel
     * @param updatedShapeModel
     */
    private static handleShapeSizeRecalculation(
        originalShapeModel: IShapeModel,
        updatedShapeModel: Partial<IShapeModel>,
    ) {
        const isLevelChange = this.hasParagraphLevelChanged(originalShapeModel, updatedShapeModel);
        let shapeModel: IShapeModel;
        if (isLevelChange && originalShapeModel.slideRef) {
            // 3.0 之后文字属性的继承是在 sdk 中进行，所以 level 改变后，paragraphList 里面部分文字属性已经过时了
            // 所以需要重新走一下 sdk 的继承逻辑拿到新的 shapeModel
            shapeModel = this.getShapeModelWithInherit(originalShapeModel, updatedShapeModel);
            // 使用继承后数据准确的 shapeModel 进行计算
            this.recalculateAndUpdateShapeSize(shapeModel);
        } else {
            // 如果 level 没变，那么认为 updatedShapeModel 上的文字属性是准确的，可以基于这个数据进行计算
            const { textBodyStyle, paragraphList, props } = updatedShapeModel;
            // 如果要修改的数据中有 props 的话，把 originalShapeModel 和 props 合并生成新的数据，基于这个数据计算
            // 这样能够避免计算时使用了过时的 props 数据
            shapeModel = props ? _merge(
                Object.create(Object.getPrototypeOf(originalShapeModel)),
                originalShapeModel, { props: updatedShapeModel?.props },
            ) : originalShapeModel;
            this.recalculateAndUpdateShapeSize(shapeModel, {
                textBodyStyle,
                paragraphList,
            });
        }
        this.recalculateAndUpdatePathList(shapeModel);
    }

    /**
     * 判断有没有段落的 level 发生变化
     * @param originalShapeModel
     * @param updatedShapeModel
     * @returns
     */
    private static hasParagraphLevelChanged(originalShapeModel: IShapeModel, updatedShapeModel: Partial<IShapeModel>) {
        const { paragraphList } = updatedShapeModel;
        return paragraphList?.some((paragraph, index) => {
            const newParagraphLevel = paragraph?.paragraphProps?.level || 0;
            const oldParagraphLevel = originalShapeModel.paragraphList?.[index]?.paragraphProps?.level || 0;
            return newParagraphLevel !== oldParagraphLevel;
        });
    }

    /**
     * 根据原始 shapeModel 和要修改的 shapeModel 数据，重新走 sdk 的继承逻辑，并更新数据到 shapeModel
     * @param originalShapeModel
     * @param updatedShapeModel
     * @returns
     */
    private static getShapeModelWithInherit(originalShapeModel: IShapeModel, updatedShapeModel: Partial<IShapeModel>) {
        // 生成修改之后的 shapeModel 数据，此时 shapeModel 中部分文字属性可能还是过时的
        const shapeModel = _merge(
            Object.create(Object.getPrototypeOf(originalShapeModel)),
            originalShapeModel, updatedShapeModel,
        );
        // 根据当前 shapeModel 数据，重新走 sdk 的继承逻辑，并更新数据到 shapeModel
        ShapePropertyConverter.appendUiDataInheritProp(shapeModel, originalShapeModel.slideRef!, false);
        // 此时 shapeModel 数据是完整准确的
        return shapeModel;
    }

    /**
     * 根据传入的新 shapeModel 属性值判断是否需要重新计算 fontScale
     */
    private static shouldRecalculateFontScale(
        originalShapeModel: IShapeModel,
        updatedShapeModel: Partial<IShapeModel>,
    ) {
        // 如果之前的 shapeModel 和修改之后的 shapeModel 都没有自缩排属性，就不需要重新计算 fontScale 了
        if (
            originalShapeModel.textBodyStyle?.autoFitType !== AutoFitType.FONT_AUTO_FIT
            && updatedShapeModel?.textBodyStyle?.autoFitType !== AutoFitType.FONT_AUTO_FIT
        ) {
            return false;
        }
        // textBodyStyle 可能是增量修改的，所以使用 isMatch 判断原有的 textBodyStyle 总共是否包含和要更新的 textBodyStyle 相同的属性值
        if (
            updatedShapeModel.textBodyStyle
            && !_isMatch(originalShapeModel.textBodyStyle as object, updatedShapeModel.textBodyStyle)
        ) {
            return true;
        }
        // style 中有很多其他属性，影响文字区域的有 width height controlValues 三个，细致判断避免无用的重复计算
        if (updatedShapeModel?.props?.style) {
            const {
                width: originalWidth,
                height: originalHeight,
                controlValues: originalControlValues,
            } = originalShapeModel.props.style;
            const {
                width: newWidth = originalWidth,
                height: newHeight = originalHeight,
                controlValues: newControlValues = originalControlValues,
            } = updatedShapeModel.props.style;
            if (
                !_isEqual(originalWidth, newWidth)
                || !_isEqual(originalHeight, newHeight)
                || !_isEqual(originalControlValues, newControlValues)
            ) {
                return true;
            }
        }
        if (updatedShapeModel.paragraphList) {
            // 如果更新的数据里有 paragraphList，大概率是需要重新计算的，就不用和原有 paragraphList 递归比较了
            return true;
        }
        return false;
    }

    /**
     * 根据传入的新 shapeModel 属性值判断是否需要重新计算形状大小并更新
     */
    private static shouldRecalculateShapeSize(
        originalShapeModel: IShapeModel,
        updatedShapeModel: Partial<IShapeModel>,
    ) {
        // 如果修改之后的形状不是根据文字调整形状大小，那么不需要重新计算形状大小并更新
        const autoFitType = updatedShapeModel?.textBodyStyle?.autoFitType
            || originalShapeModel?.textBodyStyle?.autoFitType;
        if (autoFitType !== AutoFitType.SHAPE_AUTO_FIT) {
            return false;
        }
        // textBodyStyle 可能是增量修改的，所以使用 isMatch 判断原有的 textBodyStyle 总共是否包含和要更新的 textBodyStyle 相同的属性值
        if (
            updatedShapeModel.textBodyStyle
            && !_isMatch(originalShapeModel.textBodyStyle as object, updatedShapeModel.textBodyStyle)
        ) {
            return true;
        }
        if (updatedShapeModel.paragraphList) {
            // 如果更新的数据里有 paragraphList，大概率是需要重新计算的，就不用和原有 paragraphList 递归比较了
            return true;
        }
        return false;
    }

    /**
     * 根据最新的文本内容和属性重新计算自缩排并更新
     */
    private static recalculateAndUpdateFontScale(
        originalShapeModel: IShapeModel,
        newDependentValues: FontScaleDependentValues = {},
    ) {
        const fontScaleTextBodyStyle = updateFontScaleSafety(originalShapeModel, newDependentValues);
        if (
            getFontScale(fontScaleTextBodyStyle) !== getFontScale(originalShapeModel.textBodyStyle)
            && getFontScale(fontScaleTextBodyStyle) !== getFontScale(newDependentValues.textBodyStyle)
        ) {
            originalShapeModel.updateTextBodyStyle(fontScaleTextBodyStyle);
        }
        return fontScaleTextBodyStyle;
    }

    /**
     * 根据最新的文本内容和属性重新计算形状大小并更新
     */
    private static recalculateAndUpdateShapeSize(
        originalShapeModel: IShapeModel,
        shapeSizeDependentValues: ShapeSizeDependentValues = {},
    ) {
        TextAdjustManager.getInstance()
            .calculateAndUpdateShapeStyle(originalShapeModel, true, shapeSizeDependentValues);
    }

    /**
     * 根据重新计算的形状大小更新草绘 path
     * @param originalShapeModel
     * @private
     */
    private static recalculateAndUpdatePathList(originalShapeModel: IShapeModel) {
        const style = TextAdjustManager.getInstance().adjustedModelFrameMap.get(originalShapeModel.key);
        (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.borderSketchManager?.updatePathListWhenSizeChange(
                originalShapeModel,
                {
                    width: style.width,
                    height: style.height,
                },
            );
    }
}
